"use client"

import clsx from "clsx"
import { ReactNode } from "react"
import FocusLock from "react-focus-lock"
import { useBackwardForwardCacheRestored } from "src/components/hooks/useBackwardForwardCacheRestored"
import { useKeydown } from "src/components/hooks/useKeydown"
import { usePreventBodyScroll } from "src/components/hooks/usePreventBodyScroll"
import { ModalBackdrop } from "./ModalBackdrop"
import { ModalCloseButton } from "./ModalCloseButton"

export type ModalProps = {
  children: ReactNode
  className?: `z-${number | string}`
  onClose: () => void
  alwaysFullscreen?: boolean
  anchorToTop?: boolean
  visible?: boolean
}

export const Modal = ({
  children,
  className: zClassName,
  onClose: close,
  alwaysFullscreen,
  anchorToTop,
  visible = true,
}: ModalProps) => {
  usePreventBodyScroll(visible)
  useKeydown("Escape", close)
  useBackwardForwardCacheRestored(close)

  return (
    <FocusLock autoFocus returnFocus>
      <div
        className={clsx(
          zClassName || "z-modal",
          "fixed inset-0 flex size-full max-h-full max-w-full justify-center",
          anchorToTop ? "items-start" : "items-center",
          !alwaysFullscreen && "sm:p-8",
        )}
      >
        <ModalBackdrop onClick={close} />

        <div
          className={clsx("relative flex h-screen max-h-full w-screen max-w-full flex-col items-center bg-white", {
            "overflow-hidden sm:mx-auto sm:size-auto sm:rounded-3xl sm:shadow-lg": !alwaysFullscreen,
          })}
        >
          <ModalCloseButton onClick={close} />

          {children}
        </div>
      </div>
    </FocusLock>
  )
}
